import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UserCog, ShieldCheck, ShieldOff, KeyRound, Search, AlertCircle,
  Mail, Building2, Star,
} from "lucide-react";
import StaffAccessDialog from "@/pages/owner/employees/StaffAccessDialog";
import { Link } from "react-router-dom";

export default function BranchAdminsPage() {
  const { allBranches } = useBranch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // employee-like shape for the dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/branch-admins");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) =>
      [r.employee_name, r.email, r.employee_position, ...(r.branch_names || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(ql)
    );
  }, [rows, q]);

  const openEdit = async (row) => {
    // The dialog expects an "employee" prop with id/name/branch_id/school_id
    if (!row.employee_id) {
      toast.error("Linked employee not found");
      return;
    }
    try {
      const { data: emp } = await api.get(`/employees/${row.employee_id}`).catch(() => ({ data: null }));
      if (!emp) {
        // Fallback: synthesise minimal employee shape from branch admin row
        setEditing({
          id: row.employee_id,
          name: row.employee_name,
          email: row.email,
          branch_id: row.branch_ids?.[0],
          school_id: (allBranches.find((b) => b.id === row.branch_ids?.[0]) || {}).school_id,
        });
      } else {
        setEditing(emp);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/employees/${row.employee_id}/access`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Access disabled" : "Access enabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Toggle the primary signatory for a branch — when ON, this user's name
  // prints on the lower-left of every payslip/overview for that branch. Each
  // branch can have at most one primary signatory; clicking the star on a
  // different admin reassigns it. Clicking on the currently-primary admin
  // clears it.
  const togglePrimarySignatory = async (row, branch) => {
    try {
      await api.patch(`/branches/${branch.id}/primary-admin`, {
        user_id: branch.is_primary_signatory ? null : row.user_id,
      });
      toast.success(
        branch.is_primary_signatory
          ? `Cleared primary signatory for ${branch.name}`
          : `${row.employee_name} is now the primary signatory for ${branch.name}`
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <UserCog className="w-7 h-7 text-brand-purple" /> Branch Admins
          </h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm">
            Manage staff accounts and role-based access. Add a new branch admin from the Employee list (<Link to="/employees" className="text-brand-purpleHover hover:underline">Employees</Link>) → "Manage Access".
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, branch"
            className="rounded-2xl h-11 pl-9 border-brand-purple/20"
            data-testid="ba-search"
          />
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="ba-list">
        {loading ? (
          <div className="p-10 text-center text-brand-inkMuted">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="ba-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
              <AlertCircle className="w-7 h-7 text-[#E65100]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">
              {q ? "No matches" : "No branch admins yet"}
            </p>
            <p className="text-brand-inkMuted mt-1 text-sm">
              {q
                ? "Try a different search term."
                : "Go to an employee's row in the Employee list and click Manage Access to grant login credentials and permissions."}
            </p>
            {!q && (
              <Link to="/employees" className="inline-block mt-4">
                <Button className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white">
                  Open Employee list
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
                <tr>
                  <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider">NAME</th>
                  <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">EMAIL</th>
                  <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">BRANCHES</th>
                  <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">PERMISSIONS</th>
                  <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">STATUS</th>
                  <th className="text-right px-3 py-3 font-heading font-semibold tracking-wider">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.user_id} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`ba-row-${r.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={r.employee_picture} name={r.employee_name} />
                        <div>
                          <p className="font-semibold text-foreground">{r.employee_name}</p>
                          <p className="text-xs text-brand-inkMuted">{r.employee_position || "Branch Admin"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5 text-foreground">
                        <Mail className="w-3.5 h-3.5 text-brand-inkMuted" />
                        <span className="truncate max-w-[200px]">{r.email}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {(r.branches || []).length === 0 && <span className="text-xs text-brand-inkMuted">—</span>}
                        {(r.branches || []).map((b) => (
                          <span
                            key={b.id}
                            className={`inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded-full ${
                              b.is_primary_signatory
                                ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                : "bg-brand-purpleLight/40 text-brand-purpleHover"
                            }`}
                          >
                            <Building2 className="w-3 h-3" /> {b.name}
                            <button
                              type="button"
                              onClick={() => togglePrimarySignatory(r, b)}
                              title={
                                b.is_primary_signatory
                                  ? "Primary signatory for this branch — click to clear"
                                  : "Make this admin the primary signatory for this branch"
                              }
                              aria-label={b.is_primary_signatory ? "Clear primary signatory" : "Set as primary signatory"}
                              className={`ml-0.5 rounded-full p-0.5 transition ${
                                b.is_primary_signatory
                                  ? "text-amber-600 hover:bg-amber-200"
                                  : "text-brand-inkMuted hover:bg-brand-purpleLight/60 hover:text-amber-600"
                              }`}
                              data-testid={`ba-primary-toggle-${r.user_id}-${b.id}`}
                            >
                              <Star className={`w-3.5 h-3.5 ${b.is_primary_signatory ? "fill-amber-500" : ""}`} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-block bg-[#E3F2FD] text-[#1565C0] text-xs font-semibold px-2.5 py-0.5 rounded-full" data-testid={`ba-perms-count-${r.user_id}`}>
                        {r.permissions_count}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        r.is_active
                          ? "bg-[#E8F5E9] text-[#2E7D32]"
                          : "bg-[#FFEBEE] text-[#C62828]"
                      }`}>
                        {r.is_active ? <><ShieldCheck className="w-3 h-3" /> Active</> : <><ShieldOff className="w-3 h-3" /> Disabled</>}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleActive(r)}
                          className="rounded-full border-brand-purple/30 text-xs h-8"
                          data-testid={`ba-toggle-${r.user_id}`}
                        >
                          {r.is_active ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openEdit(r)}
                          className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white text-xs h-8"
                          data-testid={`ba-manage-${r.user_id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Manage
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <StaffAccessDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); load(); } }}
        employee={editing}
        branches={allBranches}
      />
    </>
  );
}

function Avatar({ src, name }) {
  if (src) {
    return <img src={src} alt={name} className="w-9 h-9 rounded-full object-cover bg-brand-purpleLight" />;
  }
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-brand-purpleLight text-brand-purpleHover font-heading font-bold flex items-center justify-center">
      {initial}
    </div>
  );
}
